import AttachmentDataProvider from 'owa-service/lib/contract/AttachmentDataProvider';
import AttachmentDataProviderType from 'owa-service/lib/contract/AttachmentDataProviderType';
import getProviderBrandName from '../utils/getProviderBrandName';
import isFileProviderAddable, { NOT_ADDABLE_ERROR } from '../utils/isFileProviderAddable';
import strings from 'owa-strings/owa-mail-strings';
import { add } from '../actions/internalActions';
import { addFileProvider } from '../actions/publicActions';
import { confirm } from 'owa-confirm-dialog';
import { createFileProviderViewState } from '../utils/createFileProvidersViewState';
import { fileProviderAdded } from '../actions/subscribableActions';
import { FileProviderType } from '../utils/constants';
import { format } from 'owa-strings';
import { logUsage } from 'owa-analytics';
import { orchestrator } from 'satcheljs';
import { trace } from 'owa-trace';
import { AddableFileProviderInfo, getAddableFileProviderInfo } from 'owa-add-fileprovider';
import { clearAuthWindowInfo, setAuthWindowInfo, AuthWindowInfo } from 'owa-add-fileprovider';

orchestrator(addFileProvider, actionMessage => {
    addFileProviderOrchestrator(
        actionMessage.providerType,
        actionMessage.getAddableFileProviderInfoCustom,
        actionMessage.updateProvider
    );
});

export default async function addFileProviderOrchestrator(
    providerType: FileProviderType,
    getAddableFileProviderInfoCustom?: (providerType: FileProviderType) => AddableFileProviderInfo,
    updateProvider?: boolean
) {
    if (!isFileProviderAddable(providerType)) {
        throw new Error(NOT_ADDABLE_ERROR);
    }
    let attachmentDataProvider;
    try {
        attachmentDataProvider = await addFileProviderRequest(
            providerType,
            getAddableFileProviderInfoCustom,
            updateProvider
        );
    } catch (error) {
        handleError(error, providerType);
        return;
    }
    const fileProviderViewState = createFileProviderViewState(attachmentDataProvider);
    add(fileProviderViewState);
    fileProviderAdded(fileProviderViewState);
}

export function addFileProviderRequest(
    providerType: AttachmentDataProviderType,
    getAddableFileProviderInfoCustom?: (
        providerType: AttachmentDataProviderType
    ) => AddableFileProviderInfo,
    updateProvider?: boolean
): Promise<AttachmentDataProvider> {
    logUsage('FPAddDataProviderAuthWindowOpened', [providerType]);

    return new Promise((resolve, reject) => {
        getAddableFileProviderInfoCustom =
            getAddableFileProviderInfoCustom || getAddableFileProviderInfo;
        const addableFileProviderInfo = getAddableFileProviderInfoCustom(providerType);

        const { authUrl, redirectUrl, csrfToken } = addableFileProviderInfo.getAuthUrlInfo();

        const authWindowInfo: AuthWindowInfo = {
            info: {
                redirectUrl: redirectUrl,
                csrfToken: csrfToken,
                updateProvider: updateProvider,
            },
            onProviderAdded: (provider: AttachmentDataProvider) => resolve(provider),
            onErrorAddingProvider: (error: Error) => reject(error),
            getAddableFileProviderInfo: getAddableFileProviderInfoCustom,
        };

        // Remove any previous auth info and then set the new auth window info
        clearAuthWindowInfo(providerType);
        setAuthWindowInfo(providerType, authWindowInfo);

        openDialogWindowInCenter(authUrl, 600, 600);
    });
}

function openDialogWindowInCenter(url: string, width: number, height: number): Window {
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    const features = `width=${width}, height=${height}, top=${top}, left=${left}, menubar=no, toolbar=no`;

    return window.open(url, '_blank', features);
}

function handleError(error: Error, providerType: FileProviderType) {
    trace.warn(`Error in adding file provider ${error.toString()}`);
    showErrorDialog(getProviderBrandName(providerType));
    throw error;
}

function showErrorDialog(providerDisplayName: string) {
    confirm(
        strings.CouldNotConnectToAccountTitleText,
        format(strings.ErrorAddingNewProviderDescriptionText, providerDisplayName),
        false,
        {
            hideCancelButton: true,
        }
    );
}
